/**
 * Turning a Historic Encounter into a `MissionSpec`.
 *
 * **These are the only missions in the catalog that do not draw.** Every other
 * entry is a seeded generator: the seed picks the weather, the aeroplanes, the
 * opposition and where they all are. A historic encounter *is* the data — the
 * file names both pilots, both aeroplanes and what happened — so the seed
 * changes nothing about the fight. It still travels in the URL and still keys
 * the spec's id, because the standing flak belt every mission gets
 * (`mission-assemble.ts`) is placed from it, and because `/fly?m=…&seed=…`
 * should mean one thing everywhere. `historic.test.ts` asserts the flights,
 * targets, objectives and briefing are byte-identical across seeds.
 *
 * The side you fly comes in as `MissionOptions.nation`, which is exactly what
 * the original's `BRITISH` / `GERMAN` picker sets, and it selects one of the
 * file's two seat records rather than generating an opponent.
 */

import type { AircraftKey } from "../flight/aircraft-table.ts";
import type { AmmunitionType } from "../combat/targets.ts";
import { aircraftForCode } from "./aircraft-codes.ts";
import { assembleMission } from "./mission-assemble.ts";
import { buildFlight } from "./mission-build.ts";
import { historicEncounter, historicSeatRefusal } from "./historic-encounters.ts";
import {
  balloonPositionOf,
  mirrorForAllied,
  positionOf,
  seatFlightOf,
  seatOf,
  type HistoricEncounterData,
  type HistoricFlight,
  type HistoricObjective,
} from "./historic-scene.ts";
import { missionContext, type MissionOptions } from "./mission-options.ts";
import { aircraftLabel, type Nation } from "./mission-roster.ts";
import { REGION_LABELS } from "./mission-world.ts";
import type { MissionFlak, MissionFlight, MissionSpec, MissionTarget } from "./mission-spec.ts";
import type { MissionObjective } from "./objectives.ts";

/** One light battery under a historic balloon site, the same one `generators-strike.ts` places. */
const BALLOON_FLAK_SETBACK_M = 150;

interface HistoricRoster {
  readonly data: HistoricEncounterData;
  readonly player: Nation;
  readonly seatId: string;
  readonly ammunition: AmmunitionType | undefined;
  /**
   * `MissionOptions.enemyAircraft` — the manual's "put the Red Baron into a
   * Sopwith Camel" control, which applies here exactly as it does to the ace
   * duel: the encounter keeps its names, its geometry and its history, and only
   * the aeroplane under the opposition changes.
   */
  readonly enemyAircraft: AircraftKey | undefined;
}

function flightsFor(roster: HistoricRoster): MissionFlight[] {
  const { data, player, seatId, ammunition } = roster;
  return data.flights.map((flight) => {
    const framed = mirrorForAllied(positionOf(flight), flight.headingRad, player);
    const isPlayer = flight.id === seatId;
    const hostile = flight.nation !== player;
    return buildFlight({
      id: isPlayer ? "player" : flight.id,
      side: hostile ? "hostile" : "friendly",
      aircraft: hostile ? (roster.enemyAircraft ?? flight.aircraft) : flight.aircraft,
      callsign: isPlayer ? "You" : flight.callsign,
      position: framed.position,
      headingRad: framed.headingRad,
      // `buildFlight` reads an omitted skill as "this is the player's aeroplane".
      skill: isPlayer ? undefined : flight.skill,
      ammunition: isPlayer ? ammunition : undefined,
    });
  });
}

function targetsFor(data: HistoricEncounterData, player: Nation): MissionTarget[] {
  return (data.balloons ?? []).map((balloon) => {
    const framed = mirrorForAllied(balloonPositionOf(balloon), 0, player);
    return {
      id: balloon.id,
      kind: "balloon" as const,
      side: balloon.nation === player ? ("friendly" as const) : ("hostile" as const),
      position: framed.position,
      headingRad: framed.headingRad,
      label: balloon.label,
    };
  });
}

/** A light gun at the balloon site, on whichever side owns the gasbags. */
function balloonFlak(data: HistoricEncounterData, player: Nation): readonly MissionFlak[] {
  const balloons = data.balloons ?? [];
  const owner = data.balloonNation;
  if (balloons.length === 0 || !owner) return [];
  const middle = balloons.reduce((sum, item) => sum + item.z, 0) / balloons.length;
  const framed = mirrorForAllied({ x: 0, y: 0, z: middle - BALLOON_FLAK_SETBACK_M }, 0, player);
  return [{ id: "flak-balloon", side: owner === player ? "friendly" : "hostile", position: framed.position, batteryClass: "light" }];
}

function objectivesFor(objective: HistoricObjective, targets: readonly MissionTarget[]): readonly MissionObjective[] {
  const targetIds = targets.map((target) => target.id);
  if (objective.kind === "burnBalloons") {
    return [
      { kind: "destroyTargets", id: "burn", label: objective.label, role: "primary", count: targetIds.length, targetIds },
    ];
  }
  if (objective.kind === "guardBalloons") {
    return [
      { kind: "destroyAircraft", id: "drive-off", label: objective.label, role: "primary", count: objective.count },
      { kind: "protectTargets", id: "keep-balloons", label: "The balloons must survive", role: "survival", targetIds },
    ];
  }
  return [{ kind: "destroyAircraft", id: "victory", label: objective.label, role: "primary", count: objective.count }];
}

/** The incendiary belt goes to whoever came for the gasbags, and nobody else. */
function ammunitionFor(data: HistoricEncounterData, player: Nation): AmmunitionType | undefined {
  if (!data.balloonNation || (data.balloons ?? []).length === 0) return undefined;
  return data.balloonNation === player ? "regular" : "incendiary";
}

function briefingFor(data: HistoricEncounterData, player: Nation, seat: HistoricFlight): readonly string[] {
  const opponents = data.flights.filter((flight) => flight.nation !== player).length;
  return [
    `${data.title} — ${data.when}.`,
    ...data.briefing,
    ...seatOf(data, player).lines,
    `You fly a ${aircraftLabel(seat.aircraft)} over ${REGION_LABELS[data.region]}, against ${opponents === 1 ? "one aeroplane" : `${opponents} aeroplanes`}.`,
    `The winds are ${data.weather.wind}. The sky is ${data.weather.sky}.`,
  ];
}

/**
 * Builds the encounter `id` from the seat `options.nation` names. Throws on an
 * unknown id, and on the one seat the file refuses — the shell checks
 * `historicSeatPlayable` before it ever gets here, and this is the backstop.
 */
export function generateHistoric(id: string, options: MissionOptions): MissionSpec {
  const data = historicEncounter(id);
  if (!data) throw new Error(`no historic encounter "${id}"`);
  const player: Nation = options.nation ?? "german";
  const seat = seatOf(data, player);
  if (!seat.playable) throw new Error(historicSeatRefusal(player));
  const seatFlight = seatFlightOf(data, player);
  if (!seatFlight) throw new Error(`historic encounter "${id}" has no ${player} seat`);
  // The seat's own aircraft code has to agree with the aeroplane the layout
  // put the player in, or one of the two has been edited without the other.
  if (aircraftForCode(seat.aircraftCode) !== seatFlight.aircraft) {
    throw new Error(`historic encounter "${id}": ${player} seat flies ${seatFlight.aircraft}, but its aircraft code says ${aircraftForCode(seat.aircraftCode)}`);
  }

  const context = missionContext(
    {
      ...options,
      nation: player,
      era: data.era,
      region: options.region ?? data.region,
      // The encounter's own sky is a default, not a fact: `HISTORIC.DAT` carries
      // no weather, so the Mission Setup pickers keep their meaning here.
      sky: options.sky ?? data.weather.sky,
      windStrength: options.windStrength ?? data.weather.wind,
      timeOfDay: options.timeOfDay ?? data.weather.timeOfDay,
      playerAircraft: seatFlight.aircraft,
    },
    id,
  );
  const ammunition = options.ammunition ?? ammunitionFor(data, player);
  const targets = targetsFor(data, player);

  return assembleMission(context, {
    catalogId: id,
    type: (data.balloons ?? []).length > 0 ? (data.balloonNation === player ? "balloonDefense" : "balloonBusting") : "dogfight",
    title: data.title,
    flights: flightsFor({ data, player, seatId: seatFlight.id, ammunition, enemyAircraft: options.enemyAircraft }),
    targets,
    flak: balloonFlak(data, player),
    objectives: objectivesFor(seat.objective, targets),
    briefing: briefingFor(data, player, seatFlight),
    portNotes: [
      "A Historic Encounter is fixed: `HISTORIC.DAT` names both pilots, both aeroplanes and what happened, so nothing here is drawn and the seed changes only the standing flak belt.",
      "`HISTORIC.DAT` carries no positions, altitudes or aeroplane counts — the original builds those in `PS.EXE`. Every one below is this port's reading of the encounter's own prose.",
      ...data.portNotes,
    ],
  });
}
